/**
 * Phase 8 · Cluster C-47 — Team-member profile data service.
 *
 * Powers `/team/:orgUserId` (ProfilePage) and the `/reports/team`
 * MemberActivityDrawer. Every read method:
 *
 *   1. Calls `assertCanViewMemberProfile(viewer, targetUserId)` first
 *      so a missing/stale auth context can never short-circuit the
 *      RBAC gate — failures throw `RbacDenied` which `<ProfilePage>`
 *      translates into `<AccessDeniedState>`.
 *   2. Scopes every query by `org_id = viewer.orgId`. Materialised
 *      views don't honour RLS, and some denormalised columns
 *      (candidates.created_by = keycloak_sub) don't satisfy the
 *      "current_org_id()" predicate on their own — belt + braces.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum G §34.1
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.1
 *
 * Schema notes (these differ from the spec; the spec references
 * canonical column names that were never landed):
 *
 *   - `ggj_b2b_requisitions.created_by`  → TEXT (keycloak_sub)
 *   - `ggj_b2b_candidates.created_by`    → TEXT (keycloak_sub)
 *   - `ggj_b2b_applications.assigned_to` → UUID (org_user.id)
 *   - `ggj_b2b_ai_interview_sessions.created_by_org_user_id` → UUID
 *   - `ggj_b2b_email_threads.sent_by`    → UUID (org_user.id)
 *   - `ggj_b2b_object_history.changed_by_org_user_id` → UUID
 *
 * For columns keyed on `keycloak_sub` we resolve the target
 * `org_user.id → keycloak_sub` once per call via `getProfile` (cheap;
 * it's already the profile header). This keeps the public surface of
 * the service uniform: every caller passes an org_user.id.
 */
import { supabase } from '../client';
import {
  assertCanViewMemberProfile,
  type RbacViewer,
} from '@core/auth/rbac';
import type { OrgUser, ApplicationStatus } from '@core/types';

// ──────────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────────

export interface OrgUserLite {
  id: string;
  display_name: string | null;
  email: string | null;
  role: string | null;
  avatar_url: string | null;
}

export interface MemberProfile {
  user: OrgUser;
  manager: OrgUserLite | null;
  directReports: OrgUserLite[];
  /** Live workload counters for the Overview tab. */
  currentWorkload: {
    openReqs: number;
    activePipelines: number;
  };
}

export interface MemberRequisitionRow {
  id: string;
  title: string;
  status: string;
  priority: string;
  client_id: string | null;
  headcount: number | null;
  opened_at: string | null;
  target_close_date: string | null;
  closed_at: string | null;
  created_at: string;
}

export interface MemberCandidateRow {
  id: string;
  full_name: string | null;
  email: string | null;
  source: string | null;
  created_at: string | null;
  /** Which rule matched — useful for grouping the Candidates tab. */
  match: 'created' | 'assigned';
}

export interface MemberApplicationRow {
  id: string;
  status: ApplicationStatus | string | null;
  candidate_id: string | null;
  job_id: string | null;
  created_at: string | null;
  status_updated_at: string | null;
}

export interface MemberInterviewRow {
  id: string;
  candidate_id: string | null;
  status: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

export interface MemberEmailRow {
  id: string;
  subject: string | null;
  to_email: string | null;
  sent_at: string | null;
  cta_response: string | null;
  cta_responded_at: string | null;
}

export interface MemberActivityRow {
  id: string;
  entity_type: string;
  record_id: string | null;
  action: 'insert' | 'update' | 'delete' | string;
  attribute_name: string | null;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
}

/** 14-day daily buckets, oldest first. */
export interface MemberThroughput30d {
  /** Kept as 14 for parity with the <ThroughputSparklineGrid> surface. */
  days: 14;
  /** Day-0 ISO (UTC midnight) of the first bucket. */
  startDate: string;
  candidatesTouched: number[];
  interviewsRun: number[];
  emailsSent: number[];
  offersIssued: number[];
}

// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;
const THROUGHPUT_DAYS = 14 as const;

function startOfUtcDayMs(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function utcDayIso(ms: number): string {
  return new Date(startOfUtcDayMs(ms)).toISOString().slice(0, 10);
}

/** Bucket an ISO timestamp into `[0, THROUGHPUT_DAYS)` or return -1. */
function bucketOf(iso: string, startMs: number): number {
  const day = startOfUtcDayMs(new Date(iso).getTime());
  const idx = Math.floor((day - startMs) / MS_PER_DAY);
  return idx >= 0 && idx < THROUGHPUT_DAYS ? idx : -1;
}

function emptyBuckets(): number[] {
  return Array.from({ length: THROUGHPUT_DAYS }, () => 0);
}

function requireOrg(viewer: RbacViewer): string {
  if (!viewer.orgId) {
    throw new Error('teamMemberProfileService: viewer has no orgId');
  }
  return viewer.orgId;
}

/**
 * Resolves a target org_user row with all the identity columns the
 * downstream queries need (id, keycloak_sub, org_id). The `org_id`
 * filter guarantees we never resolve a user from another org even if
 * caller passes a stray id.
 */
async function resolveTarget(
  orgId: string,
  targetUserId: string,
): Promise<OrgUser | null> {
  const { data, error } = await supabase
    .from('ggj_b2b_org_users')
    .select('*')
    .eq('org_id', orgId)
    .eq('id', targetUserId)
    .maybeSingle();
  if (error) return null;
  return (data as OrgUser | null) ?? null;
}

// ──────────────────────────────────────────────────────────────────
// Service
// ──────────────────────────────────────────────────────────────────

export interface TeamMemberProfileQuery {
  viewer: RbacViewer;
  targetUserId: string;
}

export const teamMemberProfileService = {
  /**
   * Profile header + workload counters + manager/direct-reports
   * stack. Throws `RbacDenied` when the viewer is not allowed.
   */
  async getProfile({ viewer, targetUserId }: TeamMemberProfileQuery): Promise<MemberProfile> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    const user = await resolveTarget(orgId, targetUserId);
    if (!user) {
      // Same shape as RbacDenied to the UI — member simply isn't
      // reachable. We throw a plain Error so the page shows "not
      // found" rather than "denied".
      throw new Error('Member not found in this organisation.');
    }

    const [managerRes, reportsRes, openReqsRes, activePipelinesRes] = await Promise.all([
      user.manager_id
        ? supabase
            .from('ggj_b2b_org_users')
            .select('id, display_name, email, role, avatar_url')
            .eq('org_id', orgId)
            .eq('id', user.manager_id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null } as const),
      supabase
        .from('ggj_b2b_org_users')
        .select('id, display_name, email, role, avatar_url')
        .eq('org_id', orgId)
        .eq('manager_id', targetUserId)
        .eq('is_active', true)
        .order('display_name', { ascending: true }),
      // Open requisitions owned by this user (owner is denormalised to
      // created_by/keycloak_sub today — see schema notes above).
      user.keycloak_sub
        ? supabase
            .from('ggj_b2b_requisitions')
            .select('id', { count: 'exact', head: true })
            .eq('org_id', orgId)
            .eq('created_by', user.keycloak_sub)
            .in('status', ['submitted', 'approved', 'sourcing', 'on_hold'])
        : Promise.resolve({ count: 0, error: null } as const),
      // Active pipelines = applications assigned to the user and still
      // in flight (not terminal).
      supabase
        .from('ggj_b2b_applications')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .eq('assigned_to', targetUserId)
        .not('status', 'in', '("placed","rejected","withdrawn","no_show")'),
    ]);

    const manager = (managerRes.data as OrgUserLite | null) ?? null;
    const directReports = (reportsRes.data as OrgUserLite[] | null) ?? [];
    const openReqs = openReqsRes.count ?? 0;
    const activePipelines = activePipelinesRes.count ?? 0;

    return {
      user,
      manager,
      directReports,
      currentWorkload: {
        openReqs,
        activePipelines,
      },
    };
  },

  async getRequisitions(
    { viewer, targetUserId }: TeamMemberProfileQuery,
    opts: { status?: string } = {},
  ): Promise<MemberRequisitionRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    const target = await resolveTarget(orgId, targetUserId);
    if (!target?.keycloak_sub) return [];

    let q = supabase
      .from('ggj_b2b_requisitions')
      .select(
        'id, title, status, priority, client_id, headcount, opened_at, target_close_date, closed_at, created_at',
      )
      .eq('org_id', orgId)
      .eq('created_by', target.keycloak_sub)
      .order('created_at', { ascending: false })
      .limit(100);
    if (opts.status) q = q.eq('status', opts.status);

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as MemberRequisitionRow[];
  },

  async getCandidates(
    { viewer, targetUserId }: TeamMemberProfileQuery,
    opts: { limit?: number } = {},
  ): Promise<MemberCandidateRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    const target = await resolveTarget(orgId, targetUserId);
    if (!target) return [];

    const limit = Math.min(Math.max(opts.limit ?? 100, 1), 500);

    const [createdRes, assignedRes] = await Promise.all([
      target.keycloak_sub
        ? supabase
            .from('ggj_b2b_candidates')
            .select('id, full_name, email, source, created_at')
            .eq('org_id', orgId)
            .eq('created_by', target.keycloak_sub)
            .order('created_at', { ascending: false })
            .limit(limit)
        : Promise.resolve({ data: [], error: null } as const),
      // `assigned_to` on candidates is optional in the schema — the
      // migration track has it on applications, not candidates. We use
      // applications.assigned_to + a join to surface "owned candidates".
      supabase
        .from('ggj_b2b_applications')
        .select('candidate_id, created_at')
        .eq('org_id', orgId)
        .eq('assigned_to', targetUserId)
        .order('created_at', { ascending: false })
        .limit(limit),
    ]);

    if (createdRes.error) throw new Error(createdRes.error.message);
    if (assignedRes.error) throw new Error(assignedRes.error.message);

    const created = ((createdRes.data ?? []) as Array<{
      id: string;
      full_name: string | null;
      email: string | null;
      source: string | null;
      created_at: string | null;
    }>).map((c) => ({ ...c, match: 'created' as const }));

    const assignedIds = Array.from(
      new Set(
        ((assignedRes.data ?? []) as Array<{ candidate_id: string | null }>)
          .map((r) => r.candidate_id)
          .filter((x): x is string => !!x),
      ),
    );

    let assignedRows: MemberCandidateRow[] = [];
    if (assignedIds.length > 0) {
      const { data, error } = await supabase
        .from('ggj_b2b_candidates')
        .select('id, full_name, email, source, created_at')
        .eq('org_id', orgId)
        .in('id', assignedIds)
        .limit(limit);
      if (error) throw new Error(error.message);
      const createdIds = new Set(created.map((c) => c.id));
      assignedRows = ((data ?? []) as Array<{
        id: string;
        full_name: string | null;
        email: string | null;
        source: string | null;
        created_at: string | null;
      }>)
        .filter((r) => !createdIds.has(r.id))
        .map((r) => ({ ...r, match: 'assigned' as const }));
    }

    return [...created, ...assignedRows]
      .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
      .slice(0, limit);
  },

  async getApplications(
    { viewer, targetUserId }: TeamMemberProfileQuery,
    opts: { stage?: ApplicationStatus | string } = {},
  ): Promise<MemberApplicationRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    let q = supabase
      .from('ggj_b2b_applications')
      .select('id, status, candidate_id, job_id, created_at, status_updated_at')
      .eq('org_id', orgId)
      .eq('assigned_to', targetUserId)
      .order('status_updated_at', { ascending: false, nullsFirst: false })
      .limit(200);
    if (opts.stage) q = q.eq('status', opts.stage);

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as MemberApplicationRow[];
  },

  async getInterviews({ viewer, targetUserId }: TeamMemberProfileQuery): Promise<MemberInterviewRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    const { data, error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .select('id, candidate_id, status, scheduled_at, completed_at, created_at')
      .eq('org_id', orgId)
      .eq('created_by_org_user_id', targetUserId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []) as MemberInterviewRow[];
  },

  async getEmails(
    { viewer, targetUserId }: TeamMemberProfileQuery,
    opts: { limit?: number } = {},
  ): Promise<MemberEmailRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);
    const limit = Math.min(Math.max(opts.limit ?? 50, 1), 200);

    const { data, error } = await supabase
      .from('ggj_b2b_email_threads')
      .select('id, subject, to_email, sent_at, cta_response, cta_responded_at')
      .eq('org_id', orgId)
      .eq('sent_by', targetUserId)
      .order('sent_at', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []) as MemberEmailRow[];
  },

  /**
   * Recent audit rows keyed by `changed_by_org_user_id`. Note the
   * column name: the spec calls it `actor_user_id`; the actual column
   * (migration 068/091) is `changed_by_org_user_id`.
   */
  async getActivity(
    { viewer, targetUserId }: TeamMemberProfileQuery,
    opts: { limit?: number } = {},
  ): Promise<MemberActivityRow[]> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);
    const limit = Math.min(Math.max(opts.limit ?? 50, 1), 200);

    const { data, error } = await supabase
      .from('ggj_b2b_object_history')
      .select(
        'id, entity_type, record_id, action, attribute_name, old_value, new_value, created_at',
      )
      .eq('org_id', orgId)
      .eq('changed_by_org_user_id', targetUserId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []) as MemberActivityRow[];
  },

  /**
   * 14-day daily throughput across four dimensions. Queries run in
   * parallel — the service deliberately doesn't chain on getProfile
   * so the header and the sparklines can stream independently in the
   * UI (React Query runs them side-by-side).
   */
  async getThroughput30d({ viewer, targetUserId }: TeamMemberProfileQuery): Promise<MemberThroughput30d> {
    await assertCanViewMemberProfile(viewer, targetUserId);
    const orgId = requireOrg(viewer);

    const toMs = Date.now();
    const startMs = startOfUtcDayMs(toMs - (THROUGHPUT_DAYS - 1) * MS_PER_DAY);
    const fromIso = new Date(startMs).toISOString();
    const toIso = new Date(toMs).toISOString();

    const target = await resolveTarget(orgId, targetUserId);
    const sub = target?.keycloak_sub ?? null;

    const [candsRes, interviewsRes, emailsRes, offersRes] = await Promise.all([
      // "Candidates touched" ≈ object_history.action on entity_type = 'candidates'
      // by this actor in the window. Uses the audit trail rather than
      // candidates.created_by so a recruiter who edits peers' candidates
      // still counts.
      supabase
        .from('ggj_b2b_object_history')
        .select('created_at')
        .eq('org_id', orgId)
        .eq('changed_by_org_user_id', targetUserId)
        .eq('entity_type', 'candidates')
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .limit(5000),
      supabase
        .from('ggj_b2b_ai_interview_sessions')
        .select('created_at')
        .eq('org_id', orgId)
        .eq('created_by_org_user_id', targetUserId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
      supabase
        .from('ggj_b2b_email_threads')
        .select('sent_at')
        .eq('org_id', orgId)
        .eq('sent_by', targetUserId)
        .gte('sent_at', fromIso)
        .lte('sent_at', toIso),
      // Offers ≈ application status transitions into "offered" by this
      // user, surfaced via the audit log (new_value tracks status).
      // Keep the filter narrow to avoid scanning the full history.
      supabase
        .from('ggj_b2b_object_history')
        .select('created_at, new_value, attribute_name, entity_type, action')
        .eq('org_id', orgId)
        .eq('changed_by_org_user_id', targetUserId)
        .eq('entity_type', 'applications')
        .eq('attribute_name', 'status')
        .eq('new_value', 'offered')
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .limit(2000),
    ]);

    const out: MemberThroughput30d = {
      days: THROUGHPUT_DAYS,
      startDate: utcDayIso(startMs),
      candidatesTouched: emptyBuckets(),
      interviewsRun: emptyBuckets(),
      emailsSent: emptyBuckets(),
      offersIssued: emptyBuckets(),
    };

    for (const row of (candsRes.data ?? []) as Array<{ created_at: string }>) {
      const i = bucketOf(row.created_at, startMs);
      if (i >= 0) out.candidatesTouched[i]++;
    }
    for (const row of (interviewsRes.data ?? []) as Array<{ created_at: string | null }>) {
      if (!row.created_at) continue;
      const i = bucketOf(row.created_at, startMs);
      if (i >= 0) out.interviewsRun[i]++;
    }
    for (const row of (emailsRes.data ?? []) as Array<{ sent_at: string | null }>) {
      if (!row.sent_at) continue;
      const i = bucketOf(row.sent_at, startMs);
      if (i >= 0) out.emailsSent[i]++;
    }
    for (const row of (offersRes.data ?? []) as Array<{ created_at: string }>) {
      const i = bucketOf(row.created_at, startMs);
      if (i >= 0) out.offersIssued[i]++;
    }

    // Silence unused-var linter if sub is not needed elsewhere — keep
    // the resolve call to guarantee cross-org isolation for the target.
    void sub;

    return out;
  },
};
